<template><div><component-combinator component-name="DtButton" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>&lt;button&gt;</code> and link (<code v-pre>&lt;a&gt;</code>) HTML elements each describe a specific intent. Understanding the distinction is important: if it goes somewhere, it's a link (<code v-pre>&lt;a&gt;</code>). If an action occurs, use a Button (<code v-pre>&lt;button&gt;</code>). When you need <strong>button styling with navigation behavior</strong> (CTAs, toolbar actions that navigate), use DtButton's <code v-pre>href</code> or <code v-pre>to</code> props — see <a href="#navigation" class="d-docsite--link d-link">Navigation</a>.</li>
<li class="d-docsite--list-element">Avoid using too many buttons on a page.</li>
<li class="d-docsite--list-element">Set the <code v-pre>type</code> attribute to define its purpose: <code v-pre>submit</code>, <code v-pre>button</code>, or <code v-pre>reset</code>. Browsers default to <code v-pre>submit</code> if it isn't defined, and that cannot be assumed as the preferred behavior.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Conveying that an action that will occur when invoked.</li>
<li class="d-docsite--list-element">To trigger an action or behavior, such as submitting a form or spawning a <RouteLink to="/components/modal.html" class="d-docsite--link d-link">Modal</RouteLink>.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid using to navigate between destinations, deferring to a <RouteLink to="/components/link.html" class="d-docsite--link d-link">DtLink</RouteLink> instead. Exception: use DtButton with <code v-pre>href</code> or <code v-pre>to</code> when button styling is intentional (e.g. CTAs, toolbar actions).</li>
</ul>
</template>
</dialtone-usage>
<h2 id="writing-guidelines" tabindex="-1" class="d-docsite--header-2">Writing Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#writing-guidelines" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Button labels should be clear and predictable so users have confidence in their actions.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Lead with a strong verb and use <strong>verb + noun</strong> structure except for common actions like &quot;Done,&quot; &quot;Close,&quot; &quot;Cancel,&quot; or &quot;OK&quot;</li>
<li class="d-docsite--list-element">Should be sentence case</li>
<li class="d-docsite--list-element">Do not use punctuation</li>
<li class="d-docsite--list-element">Avoid unnecessary articles such as &quot;the,&quot; &quot;an,&quot; and &quot;a.&quot;</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add number</li>
<li class="d-docsite--list-element">Create menu</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add Number</li>
<li class="d-docsite--list-element">Create a menu</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone provides five options for <code v-pre>kind</code>, with three levels of <code v-pre>importance</code>.</p>
<ButtonVariantsTable></ButtonVariantsTable><h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The base button should be the go-to button for most of your needs. When in doubt, use this style. To help provide clarity to users, it is generally recommended to use only one primary button style within a section or page.</p>
<code-example source-code='&lt;dt-button&gt; Place Call &lt;/dt-button&gt;
&lt;dt-button importance=&quot;outlined&quot;&gt; Place Call &lt;/dt-button&gt;
&lt;dt-button importance=&quot;clear&quot;&gt; Place Call &lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-button> Place Call </dt-button>
  <dt-button importance="outlined"> Place Call </dt-button>
  <dt-button importance="clear"> Place Call </dt-button>
</dt-stack>
</code-example>
<h3 id="critical" tabindex="-1" class="d-docsite--header-3">Critical <a class="header-anchor d-link d-docsite--link d-link" href="#critical" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The critical button style is used to communicate critical or destructive actions such as deleting content, accounts, or canceling services.</p>
<code-example source-code='&lt;dt-button kind=&quot;critical&quot;&gt; Place Call &lt;/dt-button&gt;
&lt;dt-button kind=&quot;critical&quot; importance=&quot;outlined&quot;&gt; Place Call &lt;/dt-button&gt;
&lt;dt-button kind=&quot;critical&quot; importance=&quot;clear&quot;&gt; Place Call &lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-button kind="critical"> Place Call </dt-button>
  <dt-button kind="critical" importance="outlined"> Place Call </dt-button>
  <dt-button kind="critical" importance="clear"> Place Call </dt-button>
</dt-stack>
</code-example>
<h3 id="positive" tabindex="-1" class="d-docsite--header-3">Positive <a class="header-anchor d-link d-docsite--link d-link" href="#positive" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The positive button style is used to communicate positive actions.</p>
<code-example source-code='&lt;dt-button kind=&quot;positive&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button kind=&quot;positive&quot; importance=&quot;outlined&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button kind=&quot;positive&quot; importance=&quot;clear&quot;&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-button kind="positive">Place Call</dt-button>
  <dt-button kind="positive" importance="outlined">Place Call</dt-button>
  <dt-button kind="positive" importance="clear">Place Call</dt-button>
</dt-stack>
</code-example>
<h3 id="muted" tabindex="-1" class="d-docsite--header-3">Muted <a class="header-anchor d-link d-docsite--link d-link" href="#muted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The muted button style is used to communicate non-primary actions for contexts in which the base style may not work
(e.g. colored backgrounds, validation components, etc).
This style's use should be rare. When in doubt, use the <a href="#default" class="d-docsite--link d-link">default button style</a>.</p>
<code-example source-code='&lt;dt-button kind=&quot;muted&quot; importance=&quot;clear&quot;&gt; Place Call &lt;/dt-button&gt;
&lt;dt-button kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt; Place Call &lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-button kind="muted" importance="clear"> Place Call </dt-button>
  <dt-button kind="muted" importance="outlined"> Place Call </dt-button>
</dt-stack>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-button disabled {props}&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack gap="100">
  <dt-toggle v-model="isDisabled" :size="200" class="d-g-100 d-m-auto d-pbe-100">Disabled</dt-toggle>
  <dt-stack gap="100">
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button :disabled="isDisabled"> Place Call </dt-button>
      <dt-button :disabled="isDisabled" importance="outlined"> Place Call </dt-button>
      <dt-button :disabled="isDisabled" importance="clear"> Place Call </dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button :disabled="isDisabled" kind="critical"> Place Call </dt-button>
      <dt-button :disabled="isDisabled" kind="critical" importance="outlined"> Place Call </dt-button>
      <dt-button :disabled="isDisabled" kind="critical" importance="clear"> Place Call </dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button :disabled="isDisabled" kind="positive">Place Call</dt-button>
      <dt-button :disabled="isDisabled" kind="positive" importance="outlined">Place Call</dt-button>
      <dt-button :disabled="isDisabled" kind="positive" importance="clear">Place Call</dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button :disabled="isDisabled" kind="muted" importance="clear"> Place Call </dt-button>
      <dt-button :disabled="isDisabled" kind="muted" importance="outlined"> Place Call </dt-button>
    </dt-stack>
  </dt-stack>
</dt-stack>
</code-example>
<p class="d-docsite--paragraph">Buttons can be disabled using the <code v-pre>disabled</code> attribute or the Dialtone class, <code v-pre>d-btn--disabled</code>. Use the attribute when a button should appear disabled and not receive focus; use the class when a button should appear disabled but still receive focus (i.e. a disabled button with a tooltip).</p>
<p class="d-docsite--paragraph">Using the class also requires <code v-pre>aria-disabled</code>, and additional javascript implementation is required to prevent events.</p>
<code-example source-code='&lt;dt-stack
  gap=&quot;100&quot;
  :direction=&quot;{ &#39;default&#39;: &#39;column&#39;, &#39;md&#39;: &#39;row&#39; }&quot;
&gt;
  &lt;!-- disabled attribute --&gt;
  &lt;dt-button disabled&gt;Place Call (disabled attribute)&lt;/dt-button&gt;
  &lt;!-- disabled class --&gt;
  &lt;span v-dt-tooltip=&quot;`Tooltip example`&quot; tabindex=&quot;0&quot;&gt;
    &lt;dt-button class=&quot;d-btn--disabled&quot; aria-disabled=&quot;true&quot; tabindex=&quot;-1&quot;&gt;Place Call (disabled class)&lt;/dt-button&gt;
  &lt;/span&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <!-- disabled attribute -->
  <dt-button disabled>Place Call (disabled attribute)</dt-button>
  <!-- disabled class -->
  <span v-dt-tooltip="`Tooltip example`" tabindex="0">
    <dt-button class="d-btn--disabled" aria-disabled="true" tabindex="-1">Place Call (disabled class)</dt-button>
  </span>
</dt-stack>
</code-example>
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Buttons can be set to active state using the <code v-pre>active</code> prop or <code v-pre>.d-btn--active</code> Dialtone class.</p>
<code-example source-code='&lt;dt-button importance=&quot;clear&quot; active&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button active&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button kind=&quot;critical&quot; importance=&quot;clear&quot; active&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button kind=&quot;positive&quot; importance=&quot;clear&quot; active&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button kind=&quot;muted&quot; active&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button importance="clear" active>Place Call</dt-button>
  <dt-button active>Place Call</dt-button>
  <dt-button kind="critical" importance="clear" active>Place Call</dt-button>
  <dt-button kind="positive" importance="clear" active>Place Call</dt-button>
  <dt-button kind="muted" active>Place Call</dt-button>
</dt-stack>
</code-example>
<h3 id="link" tabindex="-1" class="d-docsite--header-3">Link <a class="header-anchor d-link d-docsite--link d-link" href="#link" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Buttons can be styled to match the appearance of a <RouteLink to="/components/link.html" class="d-docsite--link d-link">DtLink</RouteLink> in situations for which you need the appearance of a link but require the behavior of a button. Using the <code v-pre>button</code> element provides a better accessibility experience.</p>
<code-example source-code='&lt;dt-button link&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link linkKind=&quot;warning&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link linkKind=&quot;critical&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link linkKind=&quot;positive&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link linkKind=&quot;muted&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link disabled&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button link>Place Call</dt-button>
  <dt-button link linkKind="warning">Place Call</dt-button>
  <dt-button link linkKind="critical">Place Call</dt-button>
  <dt-button link linkKind="positive">Place Call</dt-button>
  <dt-button link linkKind="muted">Place Call</dt-button>
  <dt-button link disabled>Place Call</dt-button>
</dt-stack>
</code-example>
<h3 id="link-no-underline" tabindex="-1" class="d-docsite--header-3">Link no underline <a class="header-anchor d-link d-docsite--link d-link" href="#link-no-underline" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">This inverts the underline behavior. With <code v-pre>underline=&quot;false&quot;</code>, the link will not have an underline by default, but will show one on hover.</p>
<code-example source-code='&lt;dt-button link :underline=&quot;false&quot;&gt;Place Call&lt;/dt-button&gt;
&lt;dt-button link linkKind=&quot;critical&quot; :underline=&quot;false&quot;&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button link :underline="false">Place Call</dt-button>
  <dt-button link linkKind="critical" :underline="false">Place Call</dt-button>
</dt-stack>
</code-example>
<h3 id="unstyled" tabindex="-1" class="d-docsite--header-3">Unstyled <a class="header-anchor d-link d-docsite--link d-link" href="#unstyled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The unstyled button removes all default Dialtone styling while preserving the semantic HTML <code v-pre>&lt;button&gt;</code> element and maintaining proper button behavior and accessibility.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot;&gt;Place Call&lt;/dt-button&gt;'>
<dt-button kind="unstyled">Place Call</dt-button>
</code-example>
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<dt-notice kind="warning" title="Deprecated" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
<code v-pre>kind=&quot;inverted&quot;</code> has been deprecated. Use the <RouteLink to="/components/mode-island.html#inverting" class="d-docsite--link d-link">v-dt-mode directive</RouteLink> instead, or <RouteLink to="/components/mode-island.html#dtmodeisland-component" class="d-docsite--link d-link">DtModeIsland</RouteLink>.
</dt-notice>
<p class="d-docsite--paragraph">Use the <RouteLink to="/components/mode-island.html#inverting" class="d-docsite--link d-link">v-dt-mode directive</RouteLink> in place of <code v-pre>kind=&quot;inverted&quot;</code> on the component element. The previous <code v-pre>inverted</code> variant of DtButton was limited to a single presentation style. The directive now makes every combination available as an inverted style.</p>
<code-example source-code='&lt;dt-button v-dt-mode:invert {props}&gt;Place Call&lt;/dt-button&gt;'>
<dt-stack gap="100">
  <dt-toggle v-model="isInverted" :size="200" class="d-g-100 d-m-auto d-pbe-100">Inverted</dt-toggle>
  <dt-stack gap="100">
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button v-dt-mode:invert="isInverted"> Place Call </dt-button>
      <dt-button v-dt-mode:invert="isInverted" importance="outlined"> Place Call </dt-button>
      <dt-button v-dt-mode:invert="isInverted" importance="clear"> Place Call </dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button v-dt-mode:invert="isInverted" kind="critical"> Place Call </dt-button>
      <dt-button v-dt-mode:invert="isInverted" kind="critical" importance="outlined"> Place Call </dt-button>
      <dt-button v-dt-mode:invert="isInverted" kind="critical" importance="clear"> Place Call </dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button v-dt-mode:invert="isInverted" kind="positive">Place Call</dt-button>
      <dt-button v-dt-mode:invert="isInverted" kind="positive" importance="outlined">Place Call</dt-button>
      <dt-button v-dt-mode:invert="isInverted" kind="positive" importance="clear">Place Call</dt-button>
    </dt-stack>
    <dt-stack gap="100" :direction="{ 'default': 'column', 'md': 'row' }">
      <dt-button v-dt-mode:invert="isInverted" kind="muted" importance="clear"> Place Call </dt-button>
      <dt-button v-dt-mode:invert="isInverted" kind="muted" importance="outlined"> Place Call </dt-button>
    </dt-stack>
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="navigation" tabindex="-1" class="d-docsite--header-2">Navigation <a class="header-anchor d-link d-docsite--link d-link" href="#navigation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">DtButton can render as an <code v-pre>&lt;a&gt;</code> or <code v-pre>&lt;router-link&gt;</code> for cases where you need button styling with navigation behavior.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Navigating within the app?</strong> Use <code v-pre>to</code>. Renders <code v-pre>&lt;router-link&gt;</code> for client-side navigation without page reloads.</li>
<li class="d-docsite--list-element"><strong>Linking to an external site?</strong> Use <code v-pre>href</code>. Renders <code v-pre>&lt;a&gt;</code> for standard browser navigation.</li>
<li class="d-docsite--list-element"><strong>Triggering an action?</strong> Use neither. Renders <code v-pre>&lt;button&gt;</code> (default).</li>
</ul>
<h3 id="href" tabindex="-1" class="d-docsite--header-3">href <a class="header-anchor d-link d-docsite--link d-link" href="#href" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Pass <code v-pre>href</code> to render as an <code v-pre>&lt;a&gt;</code> element. Use <code v-pre>target=&quot;_blank&quot;</code> and <code v-pre>rel=&quot;noopener noreferrer&quot;</code> for external links.</p>
<code-example source-code='&lt;dt-button
  href=&quot;https://dialtone.dialpad.com&quot;
  target=&quot;_blank&quot;
  rel=&quot;noopener noreferrer&quot;
  kind=&quot;muted&quot;
  importance=&quot;outlined&quot;
  :size=&quot;200&quot;
&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;external-link&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  Dialtone
&lt;/dt-button&gt;'>
<dt-button
  href="https://dialtone.dialpad.com"
  target="_blank"
  rel="noopener noreferrer"
  kind="muted"
  importance="outlined"
  :size="200"
>
  <template #endIcon="{ iconSize }">
    <dt-icon name="external-link" :size="iconSize" />
  </template>
  Dialtone
</dt-button>
</code-example>
<h3 id="to" tabindex="-1" class="d-docsite--header-3">to <a class="header-anchor d-link d-docsite--link d-link" href="#to" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Pass <code v-pre>to</code> to render as <code v-pre>&lt;router-link&gt;</code> for internal client-side SPA navigation. Use <code v-pre>replace</code> to navigate without adding a history entry.</p>
<code-example source-code='&lt;dt-button to=&quot;/&quot; kind=&quot;default&quot; :size=&quot;100&quot;&gt;
  Home
&lt;/dt-button&gt;'>
<dt-button to="/" kind="default" :size="100">
  Home
</dt-button>
</code-example>
<h3 id="migration" tabindex="-1" class="d-docsite--header-3">Migration <a class="header-anchor d-link d-docsite--link d-link" href="#migration" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you have existing <code v-pre>&lt;a class=&quot;d-btn&quot;&gt;</code> or <code v-pre>&lt;router-link class=&quot;d-btn&quot;&gt;</code> workarounds, replace them with DtButton props:</p>
<code-example only-show="code" source-code='&lt;!-- Before: raw &lt;a&gt; with manual d-btn classes --&gt;
&lt;a
  class=&quot;d-btn d-btn--primary d-btn--outlined d-btn--sm&quot;
  href=&quot;&lt;https://example.com&gt;&quot;
  target=&quot;_blank&quot;
  rel=&quot;noopener noreferrer&quot;
&gt;
  Link Text
&lt;/a&gt;

&lt;!-- After: DtButton with href prop --&gt;
&lt;dt-button
  href=&quot;&lt;https://example.com&gt;&quot;
  target=&quot;_blank&quot;
  rel=&quot;noopener noreferrer&quot;
  importance=&quot;outlined&quot;
  :size=&quot;200&quot;
&gt;
  Link Text
&lt;/dt-button&gt;'>
<!-- Before: raw <a> with manual d-btn classes -->
<a
  class="d-btn d-btn--primary d-btn--outlined d-btn--sm"
  href="<https://example.com>"
  target="_blank"
  rel="noopener noreferrer"
>
  Link Text
</a>

<!-- After: DtButton with href prop -->
<dt-button
  href="<https://example.com>"
  target="_blank"
  rel="noopener noreferrer"
  importance="outlined"
  :size="200"
>
  Link Text
</dt-button>
</code-example>
<code-example only-show="code" source-code='&lt;!-- Before: raw &lt;router-link&gt; with manual d-btn classes --&gt;
&lt;router-link
  class=&quot;d-btn d-btn--primary d-btn--sm&quot;
  :to=&quot;roomPath&quot;
&gt;
  Join Room
&lt;/router-link&gt;

&lt;!-- After: DtButton with to prop --&gt;
&lt;dt-button :to=&quot;roomPath&quot; :size=&quot;200&quot;&gt;
  Join Room
&lt;/dt-button&gt;'>
<!-- Before: raw <router-link> with manual d-btn classes -->
<router-link
  class="d-btn d-btn--primary d-btn--sm"
  :to="roomPath"
>
  Join Room
</router-link>

<!-- After: DtButton with to prop -->
<dt-button :to="roomPath" :size="200">
  Join Room
</dt-button>
</code-example>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The default button size is <code v-pre>300</code>, but does not need to be explicitly specified.</p>
<code-example source-code='&lt;dt-button :size=&quot;100&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
  Call
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;
&lt;dt-button :size=&quot;200&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
  Call
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;
&lt;dt-button kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
  Call
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;
&lt;dt-button :size=&quot;400&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
  Call
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;
&lt;dt-button :size=&quot;500&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
  Call
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button :size="100" kind="muted" importance="outlined">
    Call
    <template #startIcon="{ iconSize }">
      <dt-icon name="phone" :size="iconSize" />
    </template>
  </dt-button>
  <dt-button :size="200" kind="muted" importance="outlined">
    Call
    <template #startIcon="{ iconSize }">
      <dt-icon name="phone" :size="iconSize" />
    </template>
  </dt-button>
  <dt-button kind="muted" importance="outlined">
    Call
    <template #startIcon="{ iconSize }">
      <dt-icon name="phone" :size="iconSize" />
    </template>
  </dt-button>
  <dt-button :size="400" kind="muted" importance="outlined">
    Call
    <template #startIcon="{ iconSize }">
      <dt-icon name="phone" :size="iconSize" />
    </template>
  </dt-button>
  <dt-button :size="500" kind="muted" importance="outlined">
    Call
    <template #startIcon="{ iconSize }">
      <dt-icon name="phone" :size="iconSize" />
    </template>
  </dt-button>
</dt-stack>
</code-example>
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<dt-notice kind="info" title="Note" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
<code v-pre>startIcon</code> and <code v-pre>endIcon</code> slots replace the previous <code v-pre>icon</code> slot and <code v-pre>icon-position</code> prop, which are deprecated and will be sunset.
</dt-notice>
<h3 id="start-and-end" tabindex="-1" class="d-docsite--header-3">Start and End <a class="header-anchor d-link d-docsite--link d-link" href="#start-and-end" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Place icons before and/or after inline of the label with <code v-pre>startIcon</code> and <code v-pre>endIcon</code> slots.</p>
<code-example source-code='&lt;span&gt;
  &lt;dt-button importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
    Label
  &lt;/dt-button&gt;
&lt;/span&gt;
&lt;span&gt;
  &lt;dt-button importance=&quot;outlined&quot;&gt;
    Label
    &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;arrow-right&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/span&gt;
&lt;span&gt;
  &lt;dt-button importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;phone&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
    Label
    &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;arrow-right&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/span&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <span>
    <dt-button importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon name="phone" :size="iconSize" />
      </template>
      Label
    </dt-button>
  </span>
  <span>
    <dt-button importance="outlined">
      Label
      <template #endIcon="{ iconSize }">
        <dt-icon name="arrow-right" :size="iconSize" />
      </template>
    </dt-button>
  </span>
  <span>
    <dt-button importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon name="phone" :size="iconSize" />
      </template>
      Label
      <template #endIcon="{ iconSize }">
        <dt-icon name="arrow-right" :size="iconSize" />
      </template>
    </dt-button>
  </span>
</dt-stack>
</code-example>
<h3 id="top-and-bottom" tabindex="-1" class="d-docsite--header-3">Top and Bottom <a class="header-anchor d-link d-docsite--link d-link" href="#top-and-bottom" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Place icons above or below the label with <code v-pre>blockStartIcon</code> and <code v-pre>blockEndIcon</code> slots.</p>
<code-example source-code='&lt;dt-button importance=&quot;outlined&quot;&gt;
  &lt;template #blockStartIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon
      name=&quot;phone&quot;
      :size=&quot;iconSize&quot;
    /&gt;
  &lt;/template&gt;
  Label
&lt;/dt-button&gt;
&lt;dt-button importance=&quot;outlined&quot;&gt;
  &lt;template #blockEndIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon
      name=&quot;phone&quot;
      :size=&quot;iconSize&quot;
    /&gt;
  &lt;/template&gt;
  Label
&lt;/dt-button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-button importance="outlined">
    <template #blockStartIcon="{ iconSize }">
      <dt-icon
        name="phone"
        :size="iconSize"
      />
    </template>
    Label
  </dt-button>
  <dt-button importance="outlined">
    <template #blockEndIcon="{ iconSize }">
      <dt-icon
        name="phone"
        :size="iconSize"
      />
    </template>
    Label
  </dt-button>
</dt-stack>
</code-example>
<h3 id="icon-only" tabindex="-1" class="d-docsite--header-3">Icon Only <a class="header-anchor d-link d-docsite--link d-link" href="#icon-only" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Icon-only buttons are commonly used for toggling actions, navigation, or closing UI elements.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;muted&quot; importance=&quot;clear&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; importance=&quot;clear&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;critical&quot; importance=&quot;clear&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;critical&quot; importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;critical&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; importance=&quot;clear&quot; kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; importance=&quot;outlined&quot; kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper
  gap="300"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="clear">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" importance="clear">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" kind="critical" importance="clear">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" kind="critical" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" kind="critical">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" importance="clear" kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" importance="outlined" kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
</dt-stack>
</code-example>
<h4 class="d-docsite--header-4">Circle</h4>
<p class="d-docsite--paragraph">The following styles are available as a circle shape.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;muted&quot; importance=&quot;clear&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;critical&quot; importance=&quot;clear&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;critical&quot; importance=&quot;outlined&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;critical&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle importance=&quot;clear&quot; kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle importance=&quot;outlined&quot; kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
  &lt;dt-button v-dt-tooltip=&quot;`Tooltip`&quot; circle kind=&quot;positive&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon
        name=&quot;phone&quot;
        :size=&quot;iconSize&quot;
      /&gt;
    &lt;/template&gt;
  &lt;/dt-button&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper
  gap="300"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="clear">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="critical" importance="clear">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="critical" importance="outlined">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="critical">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
  <dt-stack direction="row" gap="100">
    <dt-button v-dt-tooltip="`Tooltip`" circle importance="clear" kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" circle importance="outlined" kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
    <dt-button v-dt-tooltip="`Tooltip`" circle kind="positive">
      <template #startIcon="{ iconSize }">
        <dt-icon
          name="phone"
          :size="iconSize"
        />
      </template>
    </dt-button>
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="loading" tabindex="-1" class="d-docsite--header-2">Loading <a class="header-anchor d-link d-docsite--link d-link" href="#loading" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Loading buttons are useful for communicating a delay between the button interaction and its action taking place. Every button style can accept the loading button class, though we only provide a few possible examples.</p>
<h3 id="replace-button-label" tabindex="-1" class="d-docsite--header-3">Replace button label <a class="header-anchor d-link d-docsite--link d-link" href="#replace-button-label" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The width of the button remains determined by the length of the label, which is visually hidden in this state.</p>
<code-example source-code='&lt;dt-button loading&gt; Place Call &lt;/dt-button&gt;'>
<dt-stack gap="200" align="center">
  <dt-toggle :size="200" v-model="loading" class="d-g-100">
    Loading
  </dt-toggle>
  <dt-stack
    gap="300"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-stack direction="row" gap="100">
      <dt-button :loading="loading"> Place Call </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" :loading="loading">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle :loading="loading">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="100">
      <dt-button kind="muted" importance="outlined" :loading="loading"> Place Call </dt-button>
      <dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" :loading="loading">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" circle :loading="loading">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="with-label" tabindex="-1" class="d-docsite--header-3">With label <a class="header-anchor d-link d-docsite--link d-link" href="#with-label" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-button&gt;
  Validating
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-loader :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;'>
<dt-stack
  gap="100"
  direction="row"
>
<dt-button
  :size="100"
>
  Validating
  <template #endIcon="{ iconSize }">
    <dt-loader
      :size="iconSize"
    />
  </template>
</dt-button>
<dt-button
  :size="200"
>
  Validating
  <template #endIcon="{ iconSize }">
    <dt-loader
      :size="iconSize"
    />
  </template>
</dt-button>
<dt-button
  :size="300"
>
  Validating
  <template #endIcon="{ iconSize }">
    <dt-loader
      :size="iconSize"
    />
  </template>
</dt-button>
<dt-button
  :size="400"
>
  Validating
  <template #endIcon="{ iconSize }">
    <dt-loader
      :size="iconSize"
    />
  </template>
</dt-button>
</dt-stack>
</code-example>
<h2 id="leading-trailing" tabindex="-1" class="d-docsite--header-2">Leading &amp; Trailing <a class="header-anchor d-link d-docsite--link d-link" href="#leading-trailing" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the <code v-pre>#leading</code> and <code v-pre>#trailing</code> slots to render freeform content at the start or end of a button — outside the label area but inside the button's border. Common use cases include badges, count indicators, or keyboard shortcut hints. Use <code v-pre>leading-class</code> and <code v-pre>trailing-class</code> to add padding or styling to the slot containers.</p>
<dt-notice kind="info" title="Info" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
This is not to be confused with <code v-pre>#icon</code> slots, which are specifically for icons. <code v-pre>leading</code> and <code v-pre>trailing</code> are essentially freeform.
</dt-notice>
<h3 id="leading" tabindex="-1" class="d-docsite--header-3">Leading <a class="header-anchor d-link d-docsite--link d-link" href="#leading" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-button kind=&quot;muted&quot; importance=&quot;outlined&quot; leading-class=&quot;d-pis-150&quot;&gt;
  Caution
  &lt;template #leading&gt;
    &lt;span class=&quot;d-bgc-critical-strong d-bar-300 d-w12 d-h12&quot;&gt;&lt;/span&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;'>
<dt-button kind="muted" importance="outlined" leading-class="d-pis-150">
  Caution
  <template #leading>
    <span class="d-bgc-critical-strong d-bar-300 d-w12 d-h12"></span>
  </template>
</dt-button>
</code-example>
<h3 id="trailing" tabindex="-1" class="d-docsite--header-3">Trailing <a class="header-anchor d-link d-docsite--link d-link" href="#trailing" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-button :size=&quot;200&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot; trailing-class=&quot;d-pie-25&quot;&gt;
  Copy
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;copy&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  &lt;template #trailing&gt;
    &lt;dt-keyboard-shortcut shortcut=&quot;{cmd}+C&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-button&gt;'>
<dt-button :size="200" kind="muted" importance="outlined" trailing-class="d-pie-25">
  Copy
  <template #startIcon="{ iconSize }">
    <dt-icon name="copy" :size="iconSize" />
  </template>
  <template #trailing>
    <dt-keyboard-shortcut shortcut="{cmd}+C" />
  </template>
</dt-button>
</code-example>
<h2 id="split-button" tabindex="-1" class="d-docsite--header-2">Split Button <a class="header-anchor d-link d-docsite--link d-link" href="#split-button" aria-hidden="true">#</a></h2>
<dt-notice kind="info" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
<RouteLink to="/components/split-button.html" class="d-docsite--link d-link">DtSplitButton</RouteLink> is its own component containing multiple DtButtons.
</dt-notice>
<code-example only-show="demo">
<dt-split-button
  omega-tooltip-text="More calling options"
>
  Place call
  <template #dropdownList>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
    <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
  </template>
</dt-split-button>
</code-example>
<h2 id="branded" tabindex="-1" class="d-docsite--header-2">Branded <a class="header-anchor d-link d-docsite--link d-link" href="#branded" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We provide the following branded buttons for log-in and sign-up workflows.</p>
<code-example source-code='&lt;button class=&quot;d-btn d-btn--brand d-btn--google d-w100p&quot; type=&quot;button&quot;&gt;&lt;span class=&quot;d-btn__icon&quot;&gt;&lt;dt-icon name=&quot;google-glyph&quot; /&gt;&lt;/span&gt;&lt;span class=&quot;d-btn__label&quot;&gt;Log in with Google&lt;/span&gt;&lt;/button&gt;
&lt;button class=&quot;d-btn d-btn--brand d-btn--o365 d-w100p&quot; type=&quot;button&quot;&gt;&lt;span class=&quot;d-btn__icon&quot;&gt;&lt;dt-icon name=&quot;office-365&quot; /&gt;&lt;/span&gt;&lt;span class=&quot;d-btn__label&quot;&gt;Log in with Office365&lt;/span&gt;&lt;/button&gt;
&lt;button class=&quot;d-btn d-btn--brand d-btn--linkedin d-w100p&quot; type=&quot;button&quot;&gt;&lt;span class=&quot;d-btn__icon&quot;&gt;&lt;dt-icon name=&quot;linkedin&quot; /&gt;&lt;/span&gt;&lt;span class=&quot;d-btn__label&quot;&gt;Log in with LinkedIn&lt;/span&gt;&lt;/button&gt;'>
<dt-stack data-demo-wrapper
  gap="100"
  :direction="{ 'default': 'column', 'md': 'row' }"
>
  <button class="d-btn d-btn--brand d-btn--google d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="google-glyph" /></span><span class="d-btn__label">Log in with Google</span></button>
  <button class="d-btn d-btn--brand d-btn--o365 d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="office-365" /></span><span class="d-btn__label">Log in with Office365</span></button>
  <button class="d-btn d-btn--brand d-btn--linkedin d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="linkedin" /></span><span class="d-btn__label">Log in with LinkedIn</span></button>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="button" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Choosing between Link and Button elements is paramount for screenreaders to inform the user what will occur. For example: will it go somewhere (Link) or will something happen (Button)?</li>
<li class="d-docsite--list-element">Do not rely on color alone to convey the intent of the button. Defer to the button text as primary way to convey the buttons intent.</li>
<li class="d-docsite--list-element">Display a visible focus state when users tab to them.</li>
<li class="d-docsite--list-element">Use standard semantic usage of HTML elements.</li>
<li class="d-docsite--list-element">Be aware of how screenreaders handle buttons and links differently. For example, both the <code v-pre>Enter</code> and <code v-pre>Space</code> keys triggers a button, while links are triggered only by the <code v-pre>Enter</code> key.</li>
<li class="d-docsite--list-element">If it is a button type while focused:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Pressing the <code v-pre>Enter</code> or <code v-pre>Space</code> key should trigger the action.</li>
<li class="d-docsite--list-element">Pressing the <code v-pre>Tab</code> key moves focus to the next focusable element.</li>
<li class="d-docsite--list-element">Pressing the <code v-pre>Shift+Tab</code> key moves focus to the previous focusable element.</li>
</ul>
</li>
<li class="d-docsite--list-element">When using DtButton with <code v-pre>href</code> or <code v-pre>to</code>, the component automatically handles Spacebar activation and disabled state (<code v-pre>aria-disabled</code>, <code v-pre>tabindex=&quot;-1&quot;</code>). Navigating elements keep their native link role — <code v-pre>role=&quot;button&quot;</code> is not added because the element navigates rather than performing an in-page action.</li>
</ul>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="button"></component-class-table></div></template>


<script setup>
import { ref } from 'vue';
import ButtonVariantsTable from '@baseComponents/ButtonVariantsTable.vue';

const isDisabled = ref(true);
const loading = ref(true);
const isInverted = ref(true);
</script>